import * as THREE from 'three';

// LE STUDIO DES MATCAPS - partagé par Totem (sculptures) et Ribbons (rubans).
//
// Une matcap qui se lit comme du chrome photographié tient au CONTRASTE :
// ~40 % de la boule dans le quasi-noir (un sol sous une ligne d'horizon, le
// côté droit qui tourne le dos à la lumière venue du haut à gauche), deux
// boîtes à lumière LARGES (rectangles arrondis de 15 % et 8 % du diamètre de
// haut) et un liseré de Fresnel. Voir les corrections de Totem du 13/10/2026
// (une première version pastel et sans ombres donnait des objets plats).
//
// L'ENCODAGE : 8 bits ne montent pas au-dessus de 1, et 0,03 en octets
// linéaires tombe sur 8/255 - des marches dans les ombres. Les textures
// portent sqrt(v / MATCAP_GAIN), le shader rend c * c * MATCAP_GAIN.
//
// studio(x, y, px, opts) rend, pour un point (x, y) du disque (rayon 1, y
// vers le haut) : nz, ry (le vecteur réfléchi vu de face : > 0 le ciel), lit
// (1 éclairé, 0 dans l'ombre), fres (Fresnel brut) et box (0..1, la boîte à
// lumière), avec des bords de boîte doux sur 2 x edgePx pixels de matcap.
export const MATCAP_GAIN = 1.25;
export const MATCAP_DARK = 0.03, BODY_MAX = 0.6, RIM_MAX = 0.5, BOX_LUM = 1.1;
// les deux boîtes : centre, demi-largeur, demi-hauteur (unités du disque), rayon d'angle
export const BOXES = [
  { cx: -0.30, cy: 0.46, hw: 0.40, hh: 0.15, rr: 0.07 },     // 15 % du diamètre de haut
  { cx: 0.34, cy: -0.58, hw: 0.30, hh: 0.08, rr: 0.05 }      // 8 %
];
export const LUMW = [0.2126, 0.7152, 0.0722];

export function clamp01(x) { return Math.max(0, Math.min(1, x)); }
export function smooth01(e0, e1, v) { const t = clamp01((v - e0) / (e1 - e0)); return t * t * (3 - 2 * t); }
export function sdRoundBox(x, y, b) {
  const qx = Math.abs(x - b.cx) - (b.hw - b.rr), qy = Math.abs(y - b.cy) - (b.hh - b.rr);
  return Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) + Math.min(Math.max(qx, qy), 0) - b.rr;
}

// les rotations de teinte qu'un shader peut appliquer (rotation RGB autour
// de l'axe gris), échantillonnées : un corps doit rester sous le seuil de
// bloom sous TOUTES (un bleu à 0,1 de luminance devient un vert à 0,5)
const HUE_ROTS = [];
for (let k = 0; k < 24; k++) {
  const h = k / 24 * Math.PI * 2, cs = Math.cos(h), sn = Math.sin(h), a = 0.57735027;
  const m = [];
  for (let col = 0; col < 3; col++) {
    const e = [0, 0, 0]; e[col] = 1;
    const kc = [a * e[2] - a * e[1], a * e[0] - a * e[2], a * e[1] - a * e[0]];
    const kd = a * (e[0] + e[1] + e[2]) * (1 - cs);
    m.push([e[0] * cs + kc[0] * sn + a * kd, e[1] * cs + kc[1] * sn + a * kd, e[2] * cs + kc[2] * sn + a * kd]);
  }
  HUE_ROTS.push(m);
}
export function lumMaxSousRotation(r, g, b) {
  let best = 0;
  for (const m of HUE_ROTS) {
    const o0 = Math.max(0, m[0][0] * r + m[1][0] * g + m[2][0] * b), o1 = Math.max(0, m[0][1] * r + m[1][1] * g + m[2][1] * b), o2 = Math.max(0, m[0][2] * r + m[1][2] * g + m[2][2] * b);
    best = Math.max(best, LUMW[0] * o0 + LUMW[1] * o1 + LUMW[2] * o2);
  }
  return best;
}

// HSL -> RGB linéaire à luminance imposée
const _hc = new THREE.Color();
export function teinte(h, sat, lum, out) {
  _hc.setHSL(((h % 1) + 1) % 1, sat, 0.5);
  const l = LUMW[0] * _hc.r + LUMW[1] * _hc.g + LUMW[2] * _hc.b;
  const k = l > 1e-5 ? lum / l : 0;
  out[0] = _hc.r * k; out[1] = _hc.g * k; out[2] = _hc.b * k;
  return out;
}

const _st = { nz: 0, ry: 0, lit: 0, fres: 0, box: 0 };
export function studio(x, y, px, opts) {
  const o = opts || {};
  const edge = (o.edgePx != null ? o.edgePx : 1.5) * px;
  const horizon = o.horizonPx != null ? o.horizonPx * px : 0.03;
  const r2 = Math.min(1, x * x + y * y);
  const nz = Math.sqrt(1 - r2);
  const ry = 2 * nz * y;
  const ground = smooth01(-horizon, horizon, ry + 0.22);           // 0 au sol, 1 au ciel
  const side = smooth01(o.sideFar != null ? o.sideFar : 0.80, o.sideNear != null ? o.sideNear : 0.25, x * 0.9 - y * 0.2);
  let box = 0;
  for (const bx of (o.boxes || BOXES)) box = Math.max(box, smooth01(edge, -edge, sdRoundBox(x, y, bx)));
  _st.nz = nz; _st.ry = ry; _st.lit = ground * side; _st.fres = Math.pow(1 - nz, 5); _st.box = box;
  return _st;
}

export function encoder(v) { return Math.round(Math.sqrt(clamp01(v / MATCAP_GAIN)) * 255); }

// LES CANAUX POUR UN OBJET TEINTÉ DANS LE SHADER (Ribbons) : une seule
// texture RGBA, deux matériaux, la luminance seulement - la teinte de chaque
// objet est appliquée dans le shader. R = corps SATIN (encodé sqrt), G =
// boîte SATIN (0..1), B = corps CHROME (encodé sqrt), A = boîte CHROME.
//   SATIN   l'ombre douce (horizon et côté sur ~25 px), des boîtes très
//           douces (bords sur ~28 px) : du tissu satiné, des reflets larges
//   CHROME  le studio de Totem tel quel : horizon et boîtes nets (3 px)
export const SATIN_BOX = 0.85, CHROME_BOX = 1.0;
export function genererCanauxRuban(size) {
  const n = size || 256, data = new Uint8Array(n * n * 4), px = 2 / n;
  for (let j = 0; j < n; j++) {
    const y = ((j + 0.5) / n) * 2 - 1;
    for (let i = 0; i < n; i++) {
      const x = ((i + 0.5) / n) * 2 - 1, o = (j * n + i) * 4;
      // satin : un corps plus clair et plus large, l'ombre fondue
      let st = studio(x, y, px, { edgePx: 14, horizonPx: 12, sideFar: 1.0, sideNear: 0.0 });
      const vs = 0.14 + 0.34 * smooth01(1.0, -0.2, st.ry * 0.5 + x * 0.3);
      data[o] = encoder(MATCAP_DARK + (vs - MATCAP_DARK) * st.lit);
      data[o + 1] = Math.round(st.box * 255);
      // chrome : le ciel du studio de Totem, l'horizon net
      st = studio(x, y, px);
      const vc = 0.12 + 0.18 * smooth01(0.9, 0.05, st.ry);
      data[o + 2] = encoder(MATCAP_DARK + (vc - MATCAP_DARK) * st.lit);
      data[o + 3] = Math.round(st.box * 255);
    }
  }
  return { data, size: n };
}
